import { getDiscountConfig, loadCreditPlan, updateDiscountConfig } from "apis/adminServices/adminDiscountService";
import React, { useEffect, useState } from "react";
import AddDiscountModal from "./addDiscountModal";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { useToastStore } from "store/toastStore";
import ConfirmDeleteModal from "./confirmDeleteModal";
import { Link } from "react-router-dom";

interface DiscountTier {
  id: number;
  creditPlainId: number;
  credits: string;
  percentage: string;
  isActive: boolean;
  originalIndex: number;
}

const DiscountManagement: React.FC = () => {
  const [maxDiscount, setMaxDiscount] = useState("100%");
  const [discountTiers, setDiscountTiers] = useState<DiscountTier[]>([]);
  const [isEditing, setIsEditing] = useState<number | null>(null);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTierId, setSelectedTierId] = useState<number | null>(null);
  const [originalTiers, setOriginalTiers] = useState<DiscountTier[]>([]);
  const { showLoader, hideLoader } = useGlobalLoader();
  const { showToast } = useToastStore();

  // Fetch discount tiers
  const fetchDiscountTiers = async () => {
    showLoader();
    try {
      const response = await getDiscountConfig();

      // Add originalIndex to each tier
      const tiersWithIndex = response
        .filter((tier: DiscountTier) => tier.isActive)
        .map((tier: DiscountTier, index: number) => ({
          ...tier,
          originalIndex: index,
        }));

      // Sort by credits for initial display
      const sortedTiers = [...tiersWithIndex].sort((a, b) => parseFloat(a.credits) - parseFloat(b.credits));

      setDiscountTiers(sortedTiers);
      setOriginalTiers(response);
    } catch (error) {
      console.error("Error fetching discount tiers:", error);
    } finally {
      hideLoader();
    }
  };
  const predefinedCredits = [5, 10, 20, 30];
  const shouldHideAddButton = predefinedCredits.every((credit) => discountTiers.some((tier) => Number(tier.credits) === credit));

  // Call fetchDiscountTiers on initial load
  useEffect(() => {
    fetchDiscountTiers();
  }, []);

  const handleTierChange = (id: number, field: keyof DiscountTier, value: string) => {
    const newTiers = discountTiers.map((tier) => (tier.id === id ? { ...tier, [field]: value } : tier));

    // Do NOT sort during editing, maintain the original order
    setDiscountTiers(newTiers);
    setHasChanges(true);
  };

  const openDeleteModal = (id: number) => {
    setSelectedTierId(id);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSuccess = () => {
    fetchDiscountTiers(); // Refetch tiers after deletion
  };

  const handleAddSuccess = () => {
    fetchDiscountTiers();
  };

  const deleteTier = (id: number) => {
    setSelectedTierId(id);
    setIsDeleteModalOpen(true);
  };

  const editTier = (id: number) => {
    setIsEditing(id);
    setHasChanges(false);
  };

  const saveTier = async (id: number) => {
    const tierToSave = discountTiers.find((tier) => tier.id === id);
    const originalTier = originalTiers.find((tier) => tier.id === id);

    if (!tierToSave || !originalTier) return;

    const hasValueChanged =
      String(tierToSave.percentage).trim() !== String(originalTier.percentage).trim() || tierToSave.isActive !== originalTier.isActive;

    if (!hasValueChanged) {
      setIsEditing(null);
      setHasChanges(false);
      showToast("No changes to save", "info");
      return;
    }

    const requestBody = {
      id: tierToSave.id,
      creditPlaId: tierToSave.creditPlainId,
      percentage: parseFloat(tierToSave.percentage),
      credits: tierToSave.credits,
      isActive: tierToSave.isActive,
    };

    try {
      showLoader();
      await updateDiscountConfig(requestBody.id, requestBody.creditPlaId, requestBody.percentage, requestBody.isActive);

      // Refetch tiers to get updated and sorted data from backend
      await fetchDiscountTiers();

      setIsEditing(null);
      setHasChanges(false);
      showToast("Discount updated successfully", "success");
    } catch (error) {
      console.error("Error updating discount tier:", error);
      showToast("Failed to update discount tier", "error");
    } finally {
      hideLoader();
    }
  };

  return (
    <div>
      <section className="flex justify-between items-start px-6 py-5 bg-gradient-to-r from-[#F9FBFF] to-[#F1F6FF] rounded-lg shadow-sm border border-blue-50">
        <h1 className="text-xl font-semibold text-grease-100 tracking-tight">Discount Management</h1>

        <nav className="flex items-center text-sm text-lightgray-200 space-x-2 mt-1">
          <span className="st-dashboard text-lightgray-200 text-sm" aria-hidden="true" />
          <Link to="/" className="hover:text-royalblue-100 transition-colors duration-150">
            Dashboard
          </Link>
          <span className="st-arrow-right text-lightgray-200 text-xs" aria-hidden="true" />
          <span className="text-royalblue-100 font-medium">Discount Management</span>
        </nav>
      </section>

      <section className="bg-white p-6 rounded-xl mt-6">
        {/* <div>
          <label className="text-xs font-light text-gray-500">Maximum Discount Percentage</label>
          <input
            className="bg-gray-100 text-gray-400 border-none cursor-not-allowed text-xs rounded-md block w-[340px] p-2.5 mt-2"
            value={maxDiscount}
            disabled
            onChange={(e) => setMaxDiscount(e.target.value)}
          />
        </div> */}

        <div className="grid grid-cols-2 gap-5">
          {discountTiers.map((tier: DiscountTier) => (
            <div key={tier.id} className="grid gap-4 md:grid-cols-2 border border-gray-200 rounded-md p-4 mt-6 relative">
              <div>
                <p className="text-xs text-grease-100 font-medium">If the user purchases</p>
                <label className="text-xs font-light text-gray-500">Tokens</label>
                <input
                  className="bg-gray-100 text-gray-400 border-none cursor-not-allowed text-xs rounded-md block w-full p-2.5 mt-2"
                  value={tier.credits}
                  disabled
                />
              </div>
              <div>
                <p className="text-xs text-grease-100 font-medium">Discount Percentage</p>
                <label className="text-xs font-light text-gray-500">Discount %</label>
                <input
                  className={`text-xs rounded-md block w-full p-2.5 mt-2 border-none ${
                    isEditing === tier.id ? "bg-blue-50 text-gray-600" : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                  value={tier.percentage}
                  onChange={(e) => handleTierChange(tier.id, "percentage", e.target.value)}
                  disabled={isEditing !== tier.id}
                />
              </div>

              <div className="absolute top-2 right-2 flex gap-2">
                {isEditing === tier.id ? (
                  <button
                    className="w-8 h-8 rounded-md border border-green-300 hover:bg-green-50"
                    title="Save"
                    onClick={() => saveTier(tier.id)}
                  >
                    <span className="st-check text-green-600 text-xs" />
                  </button>
                ) : (
                  <button
                    className="w-8 h-8 rounded-md border border-blue-300 hover:bg-blue-50"
                    title="Edit"
                    onClick={() => editTier(tier.id)}
                  >
                    <span className="st-pen text-royalblue-100 text-xs" />
                  </button>
                )}
                <button
                  className="w-8 h-8 rounded-md border border-red-300 hover:bg-red-50"
                  onClick={() => openDeleteModal(tier.id)}
                  title="Delete"
                >
                  <span className="st-delete text-red-600 text-xs" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {!shouldHideAddButton && (
          <button
            className="flex items-center gap-2 px-6 py-2 mt-6 text-xs bg-[#FFD700] text-white border rounded-md"
            onClick={() => setIsModalOpen(true)}
          >
            <span className="st-add text-white" />
            Add
          </button>
        )}

        {/* Modal */}
        {isModalOpen && <AddDiscountModal onClose={() => setIsModalOpen(false)} onDiscountAdded={handleAddSuccess} />}

        {isDeleteModalOpen && selectedTierId !== null && (
          <ConfirmDeleteModal tierId={selectedTierId} onClose={() => setIsDeleteModalOpen(false)} onDeleteSuccess={handleDeleteSuccess} />
        )}
      </section>
    </div>
  );
};

export default DiscountManagement;
